import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CustomLabelTextTable from '../misc/CustomLabelTextTable';
import SoftwareVersion from '@common/interfaces/cleaning-map/software-version';
import HardwareVersion from '../../models/robots/hardware-version';

const SystemInformationDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  softwareVersion: SoftwareVersion | null;
  hardwareVersion: HardwareVersion | null;
  serialNumber?: string | null;
}> = ({ open, softwareVersion, hardwareVersion, serialNumber, onClose }) => {
  const { t } = useTranslation();

  const get_hardware_version_localization = (type: string, value: string | null | undefined) => {
    if (value == null || value.length === 0) {
      return t('HardwareVersioningOption-not-configured');
    }
    const key = `HardwareVersioningOption.${type}.${value}`;
    if (t(key) !== key) {
      return t(key);
    }
    return value;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('title-robot-system-information')}</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <CustomLabelTextTable
            cellPadding={1}
            rows={[
              { label: t('table-header-serial-number'), text: serialNumber },
              { label: t('table-header-robot-version'), text: hardwareVersion?.robotVersion },
              { label: t('table-header-robot-generation'), text: hardwareVersion?.robotGeneration },
              { label: t('table-header-build-version'), text: softwareVersion?.buildVersion },
              {
                label: t('table-header-rsw-software-version'),
                text: softwareVersion?.rswSoftwareVersion,
              },
              {
                label: t('table-header-web-software-version'),
                text: softwareVersion?.webSoftwareVersion,
              },
              {
                label: t('table-header-mgmt-software-version'),
                text: softwareVersion?.mgmtSoftwareVersion,
              },
              {
                label: t('table-header-proxy-software-version'),
                text: softwareVersion?.proxySoftwareVersion,
              },
              { label: t('table-header-firmware-version'), text: hardwareVersion?.firmwareVersion },
              {
                label: t('table-header-baseboard-version'),
                text: hardwareVersion?.baseboardVersion,
              },
              {
                label: t('table-header-powerboard-version'),
                text: hardwareVersion?.powerboardVersion,
              },

              {
                label: t('table-header-camera-version'),
                text: get_hardware_version_localization('camera', hardwareVersion?.cameraVersion),
              },
              {
                label: t('table-header-lidar-version'),
                text: get_hardware_version_localization('lidar', hardwareVersion?.lidarVersion),
              },
              {
                label: t('table-header-som-version'),
                text: get_hardware_version_localization('som', hardwareVersion?.somVersion),
              },
              {
                label: t('table-header-battery-serial-number'),
                text: hardwareVersion?.batterySerialNumber,
              },
              {
                label: t('table-header-wpa-enabled'),
                text:
                  hardwareVersion?.wpaEnabled == null
                    ? t('unknown-value')
                    : hardwareVersion.wpaEnabled
                      ? t('wpa_enabled_true')
                      : t('wpa_enabled_false'),
              }, // true == wifi false, gsm
              {
                label: t('table-header-gsm-dongle'),
                text: get_hardware_version_localization('gsm_dongle', hardwareVersion?.gsmDongle),
              },
              {
                label: t('table-header-wlan-antenna-1'),
                text: get_hardware_version_localization(
                  'wlan_dongle_1',
                  hardwareVersion?.wlanAntenna1,
                ),
              },
              {
                label: t('table-header-wlan-antenna-2'),
                text: get_hardware_version_localization(
                  'wlan_dongle_2',
                  hardwareVersion?.wlanAntenna2,
                ),
              },
              { label: t('table-header-filter-cleaner'), text: hardwareVersion?.filterCleaner },
              {
                label: t('table-header-flasher'),
                text: get_hardware_version_localization('flasher', hardwareVersion?.flasher),
              },
              {
                label: t('table-header-front-lights'),
                text: get_hardware_version_localization(
                  'front_lights',
                  hardwareVersion?.frontLights,
                ),
              },
              {
                label: t('table-header-fan'),
                text: get_hardware_version_localization('blower', hardwareVersion?.fan),
              },
              {
                label: t('table-header-cover'),
                text: get_hardware_version_localization('cover', hardwareVersion?.cover),
              },
              {
                label: t('table-header-front-brushes'),
                text: get_hardware_version_localization(
                  'front_brushes',
                  hardwareVersion?.frontBrushes,
                ),
              },
              {
                label: t('table-header-roller-brush'),
                text: get_hardware_version_localization(
                  'roller_brush',
                  hardwareVersion?.rollerBrush,
                ),
              },
              {
                label: t('table-header-wheels'),
                text: get_hardware_version_localization('wheel', hardwareVersion?.wheels),
              },
            ]}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t('button-close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SystemInformationDialog;
